import styled, { useTheme } from 'styled-components';
import Button from '../../../../components/Button';
import ColorizedSpan from '../../../../components/Colorized';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import WidthContainer from '../../../../components/WidthContainer';
import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../common/responsive';
import { hapticsImpactLight } from '../../../../common/haptic';
import { useNavigate } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';
import { useSelector } from 'react-redux';
import { selectThemeMode } from '../../../../redux/slices/themeSlice';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-direction: row;
  width: 100%;
  height: fit-content;
  min-height: 500px;
  position: relative;
  justify-content: space-between;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    padding-top: 50px;
    text-align: center;
  }
`;

const Title = styled.h1`
  font-size: 64px;
  font-weight: 500;
  line-height: 77px;
  letter-spacing: -2%;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 2.5rem;
    line-height: 2.8rem;
  }
`;

const SubTitle = styled.h2`
  font-size: 20px;
  font-weight: 400;
  line-height: 29px;
  letter-spacing: -2%;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 1rem;
    line-height: 1.2rem;
  }
`;

const Column = styled.div`
  width: 100%;
  display: flex;
  gap: 30px;
  flex-direction: column;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 15px;
  }
`;

const MobileImage = styled.img`
  width: 436px;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  cursor: pointer;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
    margin-top: 40px;
    margin-bottom: 40px;
  }
`;

const ContainerBackground = styled.div`
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.background.primary} 0%,
    ${({ theme }) => theme.primary.background} 100%
  );
  position: absolute;
  width: 100%;
  left: 0px;
`;

const BackedByAlliance = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 12px;
  margin-bottom: 20px;
  position: absolute;
  bottom: 0px;
  left: 0px;
  right: 0px;
  z-index: 1;
`;

const IMAGER_MAPPER = {
  default: '/landing-v2/hero-mobile-bitcoin.webp',
  stocks: '/landing-v2/hero-mobile-tesla.webp',
  netflix: '/landing-v2/hero-mobile-netflix.webp',
  gold: '/landing-v2/hero-mobile-gold.webp',
  mcdonalds: '/landing-v2/hero-mobile-mcdonalds.webp',
  microsoft: '/landing-v2/hero-mobile-microsoft.webp',
  cocacola: '/landing-v2/hero-mobile-cocacola.webp',
  apple: '/landing-v2/hero-mobile-apple.webp',
};

const HEADLINE_MAPPER = {
  default: 'Crypto',
  stocks: 'Stocks',
  netflix: 'Stocks',
  gold: 'Stocks',
  mcdonalds: 'Stocks',
  microsoft: 'Stocks',
  cocacola: 'Stocks',
  apple: 'Stocks',
};

const StocksSubline = (theme) => (
  <SubTitle style={{ color: theme.text.regular }}>
    Instant, personalized stock gifts. No brokerage accounts, <br /> powered by
    blockchain technology.
  </SubTitle>
);

const SUBLINE_MAPPER = {
  default: (theme) => (
    <SubTitle style={{ color: theme.text.regular }}>
      Personalized gift cards loaded with crypto, stocks, or other digital
      assets.
    </SubTitle>
  ),
  stocks: StocksSubline,
  netflix: StocksSubline,
  gold: StocksSubline,
  mcdonalds: StocksSubline,
  microsoft: StocksSubline,
  cocacola: StocksSubline,
  apple: StocksSubline,
};

const Hero = () => {
  const backgroundRef = useRef(null);
  const [siblingHeight, setSiblingHeight] = useState(undefined);
  const [searchParams] = useSearchParams();
  const lpd = searchParams.get('lpd');
  const navigate = useNavigate();
  const showFreeGift = useFlag('showFreeGift');
  const selectedTheme = useSelector(selectThemeMode);

  useEffect(() => {
    const updateHeight = () => {
      if (backgroundRef.current) {
        setSiblingHeight(backgroundRef.current.offsetHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);

    if (backgroundRef.current) {
      observer.observe(backgroundRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const theme = useTheme();

  const buildCreateUrl = () => {
    const params = new URLSearchParams();
    if (lpd) {
      params.set('lpd', lpd);
    }
    if (showFreeGift) {
      params.set('trial', '1');
    }
    const query = params.toString();
    return query ? `/app/gifts/create?${query}` : '/app/gifts/create';
  };

  return (
    <>
      <ContainerBackground ref={backgroundRef}>
        <WidthContainer>
          <Container>
            <Column>
              <Title>
                {lpd ? (
                  <>
                    The Easiest Way <br />
                    to <ColorizedSpan>Gift</ColorizedSpan>{' '}
                    {HEADLINE_MAPPER[lpd]}
                  </>
                ) : (
                  <>
                    The Easiest Way <br />
                    to <ColorizedSpan>Gift</ColorizedSpan> Crypto
                  </>
                )}
              </Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {SUBLINE_MAPPER[lpd || 'default'](theme)}
                {showFreeGift && (
                  <SubTitle
                    style={{ color: theme.text.regular, fontWeight: 600 }}
                  >
                    Your first digital gift is on us.
                  </SubTitle>
                )}
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                <Button
                  style={{ marginTop: isMobile ? '20px' : '0px' }}
                  clickAnimation='bouncy'
                  onClick={() => {
                    navigate(buildCreateUrl());
                  }}
                  fullWidth={isMobile}
                >
                  {showFreeGift
                    ? 'Create Your First Gift Free'
                    : 'Create Your First Gift'}
                </Button>
              </div>
            </Column>
            <Column style={{ width: 'fit-content' }}>
              <MobileImage
                onClick={() => {
                  hapticsImpactLight();
                  navigate(buildCreateUrl());
                }}
                src={IMAGER_MAPPER[lpd || 'default']}
              />
            </Column>
          </Container>
        </WidthContainer>
        <BackedByAlliance>
          <a href='https://alliance.xyz/companies/deks' target='_blank'>
            <img
              width={200}
              src={`https://alliance.xyz/share/backed-by.png${selectedTheme === 'light' ? '?mode=light' : ''}`}
              alt='Backed by Alliance'
            />
          </a>
        </BackedByAlliance>
      </ContainerBackground>
      <div style={{ height: siblingHeight }} />
    </>
  );
};

export default Hero;
